/**
 * GMA-1616 (CompassCare Web 1/14 — Foundation) — shared DataTable base.
 *
 * The table primitive for the facelift's dense screens (Clients, Devices,
 * Alerts, Audit). Column config + rows are injected. Styling comes from the
 * single design-token set: numeric columns use the IBM Plex Mono token, and a
 * per-row severity (via `getRowSeverity`) paints a left accent from the
 * severity token set — no per-page colors.
 *
 * Layout: the table has a min width and lives in an overflow-x:auto container,
 * so below the min shell width it scrolls horizontally and never collapses
 * columns (GMA-1616 shell rule).
 *
 * Accessibility: a real <table> named by `ariaLabel`, column headers with
 * scope="col". When `onRowClick` is set the FIRST column's cell becomes a
 * <button> (with an aria-label), so rows are keyboard-operable without an
 * ambiguous role="button" on the <tr> that would clobber native row semantics
 * — matching the ClientsTable / VitalsTable pattern.
 */
import React from "react";
import PropTypes from "prop-types";
import { theme, severityToken, SEVERITY_LEVELS } from "../../styles/designTokens";

export default function DataTable({
  columns = [],
  rows = [],
  ariaLabel,
  getRowSeverity,
  onRowClick,
  getRowLabel,
  rowKey = (row) => row.id,
  emptyMessage = "No results",
  minWidth = 720,
}) {
  const clickable = typeof onRowClick === "function";

  return (
    <div
      data-testid="data-table-scroll"
      style={{
        overflowX: "auto",
        width: "100%",
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.surface,
      }}
    >
      <table
        aria-label={ariaLabel}
        style={{
          width: "100%",
          minWidth,
          borderCollapse: "collapse",
          color: theme.textPrimary,
          fontFamily: theme.fontUI,
          fontSize: 14,
        }}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{
                  textAlign: col.align || "left",
                  padding: "10px 12px",
                  borderBottom: `1px solid ${theme.border}`,
                  fontFamily: theme.fontUI,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  color: theme.textMuted,
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length || 1}
                style={{ padding: "16px 12px", color: theme.textMuted }}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const level = getRowSeverity ? getRowSeverity(row) : null;
              // Only paint an accent for a real severity level; an
              // unknown/undefined value leaves the row un-accented (transparent)
              // rather than a misleading default color.
              const accent =
                level && SEVERITY_LEVELS.includes(level)
                  ? severityToken(level).color
                  : "transparent";
              const key = rowKey(row);
              return (
                <tr
                  key={key}
                  data-testid={`data-row-${key}`}
                  style={{
                    borderLeft: `3px solid ${accent}`,
                    borderBottom: `1px solid ${theme.border}`,
                  }}
                >
                  {columns.map((col, colIndex) => {
                    const value = row[col.key];
                    const cellStyle = {
                      textAlign: col.align || "left",
                      padding: "10px 12px",
                      fontFamily: col.mono ? theme.fontMono : theme.fontUI,
                      fontVariantNumeric: col.mono ? "tabular-nums" : undefined,
                      whiteSpace: "nowrap",
                    };
                    // The first column is the row header (<th scope="row">) for
                    // proper table semantics; when the row is clickable it also
                    // hosts the native <button> that operates the row (the <tr>
                    // keeps its row role).
                    const isFirst = colIndex === 0;
                    const isActionCell = clickable && isFirst;
                    // Fall back to the first cell's value for the button label
                    // only when it's a primitive; a React node/object would
                    // stringify to "[object Object]", so use a generic label.
                    const primitiveValue =
                      value === null ||
                      value === undefined ||
                      typeof value === "object"
                        ? ""
                        : String(value);
                    const CellTag = isFirst ? "th" : "td";
                    const cellProps = isFirst
                      ? { scope: "row", style: { ...cellStyle, fontWeight: 400 } }
                      : { style: cellStyle };
                    // Default label from the primitive cell value; a custom
                    // getRowLabel wins ONLY when it returns a non-empty string,
                    // so it can never leave the button without an accessible name.
                    const defaultLabel = primitiveValue
                      ? `Open ${primitiveValue}`
                      : "Open row";
                    const customLabel = getRowLabel && getRowLabel(row);
                    const actionLabel =
                      typeof customLabel === "string" && customLabel.trim()
                        ? customLabel
                        : defaultLabel;
                    return (
                      <CellTag key={col.key} {...cellProps}>
                        {isActionCell ? (
                          <button
                            type="button"
                            onClick={() => onRowClick(row)}
                            aria-label={actionLabel}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              margin: 0,
                              font: "inherit",
                              color: "inherit",
                              cursor: "pointer",
                              textAlign: "inherit",
                              display: "block",
                              width: "100%",
                            }}
                          >
                            {value}
                          </button>
                        ) : (
                          value
                        )}
                      </CellTag>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      align: PropTypes.oneOf(["left", "right", "center"]),
      /** Render values in the IBM Plex Mono token (numerics/timestamps). */
      mono: PropTypes.bool,
    }),
  ),
  rows: PropTypes.arrayOf(PropTypes.object),
  /** Accessible name for the table (required — the table has no visible caption). */
  ariaLabel: PropTypes.string.isRequired,
  /** (row) => severity level string; paints the row's left accent. */
  getRowSeverity: PropTypes.func,
  /** (row) => void; makes the first cell a keyboard-operable button. */
  onRowClick: PropTypes.func,
  /** (row) => string; aria-label for the row's action button (defaults to "Open <first cell>"). */
  getRowLabel: PropTypes.func,
  /** (row) => stable key; defaults to row.id. */
  rowKey: PropTypes.func,
  emptyMessage: PropTypes.node,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
